import { getPersonaIcon, getPersonaDisplayName, PERSONA_CORPORATE_COLORS } from '../constants/personas'
import { useTheme } from '../contexts/ThemeContext'
import type { LucideProps } from 'lucide-react'
import {
  Network,
  Compass,
  DraftingCompass,
  GitBranch,
  Layout,
  FileText,
  ShieldCheck,
  Eye,
  GitMerge,
  Bug,
  Wrench,
  Database,
  Server,
  Circle,
} from 'lucide-react'
import type { ComponentType } from 'react'

export type PersonaIconSize = 'sm' | 'md' | 'lg' | 'xl'

interface PersonaIconProps {
  persona: string | null | undefined
  size?: PersonaIconSize
  className?: string
}

const SIZE_CLASSES: Record<PersonaIconSize, string> = {
  sm: 'w-5 h-5',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
}

const LUCIDE_ICON_SIZES: Record<PersonaIconSize, number> = {
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
}

/** Direct mapping from persona key to Lucide component — tree-shakeable */
const PERSONA_ICON_COMPONENTS: Record<string, ComponentType<LucideProps>> = {
  nexus: Network,
  voyager: Compass,
  architect: DraftingCompass,
  prism: GitBranch,
  visionary: Layout,
  cantor: FileText,
  sentinel: ShieldCheck,
  lurker: Eye,
  weaver: GitMerge,
  chaos: Bug,
  demiurge: Wrench,
  gatekeeper: Database,
  terraformer: Server,
}

export function PersonaIcon({ persona, size = 'md', className = '' }: PersonaIconProps) {
  const { theme } = useTheme()
  const isCorporate = theme === 'corporate'
  const personaKey = persona?.toLowerCase() || 'nexus'
  const personaName = getPersonaDisplayName(persona, isCorporate)
  const sizeClass = SIZE_CLASSES[size]

  if (isCorporate) {
    const colors = PERSONA_CORPORATE_COLORS[personaKey] || { bg: '#f1f5f9', icon: '#475569' }
    const LucideIcon = PERSONA_ICON_COMPONENTS[personaKey] || Circle
    const iconSize = LUCIDE_ICON_SIZES[size]

    return (
      <div
        className={`${sizeClass} rounded-full flex items-center justify-center flex-shrink-0 ${className}`}
        style={{ backgroundColor: colors.bg }}
        title={personaName}
      >
        <LucideIcon size={iconSize} color={colors.icon} strokeWidth={2} />
      </div>
    )
  }

  // Sci-fi themes: render PNG icon
  const iconSrc = getPersonaIcon(persona)

  return (
    <img
      src={iconSrc}
      alt={`${personaName} persona`}
      className={`${sizeClass} object-contain ${className}`}
    />
  )
}
